import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ENV_CONFIG } from '@digilize/shared/utils/tokens';
import { EnvConfig } from '@shared/definitions';
import { from, Observable, of, switchMap } from 'rxjs';

import { PrefetchKey, takePrefetchedRead } from './bootstrap-prefetch';

/**
 * Answers the checkout's opening reads from what `index.html` already fetched during HTML parse (see
 * {@link takePrefetchedRead}), which takes the whole first API wave off the critical path — it ran
 * against the bundle download instead of after it.
 *
 * Matched on the URL the request would have gone to rather than on a stored key, so a read the index
 * never started, or started for another POS or show, is simply requested as before. Sits above
 * `ApiInterceptor` and below `ShopAuthInterceptor`: the `Authorization` header is set by then, and
 * comparing it is what keeps an answer fetched for one session from being handed to another. The
 * error interceptors above see nothing, having nothing to see — a prefetch that failed is not used.
 */
@Injectable()
export class BootstrapPrefetchInterceptor implements HttpInterceptor {
  constructor(@Inject(ENV_CONFIG) private readonly config: EnvConfig) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const key = request.method === 'GET' ? this.matchPrefetchedRead(request) : null;
    const prefetched = key && takePrefetchedRead(key, request.headers.get('Authorization'));

    if (!prefetched) {
      return next.handle(request);
    }

    return from(prefetched).pipe(
      switchMap((read) =>
        read
          ? // Only the body is ever read from these responses, so the headers are not reconstructed.
            of<HttpEvent<unknown>>(
              new HttpResponse({ body: read.body, status: 200, statusText: 'OK', url: request.urlWithParams })
            )
          : next.handle(request)
      )
    );
  }

  /** Which of the prefetched reads this request is, or null when it is any other call. */
  private matchPrefetchedRead(request: HttpRequest<unknown>): PrefetchKey | null {
    const prefetch = window.__plysPrefetch;

    if (!prefetch) {
      return null;
    }

    const base = `${this.config.api}/v1.0`;
    const { posId, showId } = prefetch;

    if (request.url === `${base}/pos/${posId}`) {
      return 'pos';
    }

    if (request.url === `${base}/pos/${posId}/add-ons`) {
      return 'addons';
    }

    if (!showId) {
      return null;
    }

    if (request.url === `${base}/shows/${showId}` && request.params.get('pos') === posId) {
      return 'show';
    }

    if (
      request.url === `${base}/shows/${showId}/products` &&
      request.params.get('pos') === posId &&
      request.params.get('include_availability') === 'true'
    ) {
      return 'products';
    }

    if (request.url === `${base}/seats/reservations/${showId}`) {
      return 'taken';
    }

    return null;
  }
}
